/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
export var LEVEL_COLORS = ['primary', 'success', 'warning', 'danger'];
export var EuiRangeLevels = ({
  levels = [],
  max,
  min,
  showTicks,
  compressed
}) => {
  const validateLevelIsInRange = level => {
    if (level.min < min) {
      throw new Error(`The level min of ${level.min} is lower than the min value of ${min}.`);
    }

    if (level.max > max) {
      throw new Error(`The level max of ${level.max} is higher than the max value of ${max}.`);
    }
  };

  const classes = classNames('euiRangeLevels', {
    'euiRangeLevels--hasTicks': showTicks,
    'euiRangeLevels--compressed': compressed
  });
  return <div className={classes}>
      {levels.map((level, index) => {
      validateLevelIsInRange(level);
      const range = level.max - level.min;
      const width = range / (max - min) * 100;
      return <span key={index} style={{
        width: `${width}%`
      }} className={`euiRangeLevel euiRangeLevel--${level.color}`} />;
    })}
    </div>;
};
EuiRangeLevels.propTypes = {
  levels: PropTypes.arrayOf(PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    color: PropTypes.oneOf(["primary", "success", "warning", "danger"]).isRequired
  }).isRequired),
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  showTicks: PropTypes.bool,
  compressed: PropTypes.bool
};

try {
  // @ts-ignore
  EuiRangeLevels.displayName = "EuiRangeLevels"; // @ts-ignore

  EuiRangeLevels.__docgenInfo = {
    "description": "",
    "displayName": "EuiRangeLevels",
    "props": {
      "levels": {
        "defaultValue": {
          value: "[]"
        },
        "description": "",
        "name": "levels",
        "required": false,
        "type": {
          "name": "EuiRangeLevel[] | undefined"
        }
      },
      "max": {
        "defaultValue": null,
        "description": "",
        "name": "max",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "min": {
        "defaultValue": null,
        "description": "",
        "name": "min",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "showTicks": {
        "defaultValue": null,
        "description": "",
        "name": "showTicks",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "compressed": {
        "defaultValue": null,
        "description": "",
        "name": "compressed",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/form/range/range_levels.tsx#EuiRangeLevels"] = {
      docgenInfo: EuiRangeLevels.__docgenInfo,
      name: "EuiRangeLevels",
      path: "src/components/form/range/range_levels.tsx#EuiRangeLevels"
    };
} catch (__react_docgen_typescript_loader_error) {}